import React, { useState } from "react";
import { Button, Typography } from "antd";
import * as S from "./styles";
import { useQuery } from "react-query";
import { getCarrierOptions } from "api/logistics/carrier-options";
import { cloneDeep } from "lodash-es";
import ShippingPreferencesDrawer from "./ShippingPreferencesDrawer";
import ShippingPreferencesSearch from "./ShippingPreferencesSearch";
import ShippingPreferencesTable from "./ShippingPreferencesTable";

const { Text } = Typography;

interface ShippingPreferenceProps {
  whitelisted: string[];
  blacklisted: string[];
  onSelectWhitelist: (checkedValue: Array<string>) => void;
  onSelectBlacklist: (checkedValue: Array<string>) => void;
}

export interface CarrierName {
  carrierName: string;
}

const ShippingPreferencesSection: React.FC<ShippingPreferenceProps> = ({
  whitelisted,
  blacklisted,
  onSelectWhitelist,
  onSelectBlacklist,
}) => {
  const [carrierOptions, setCarrierOptions] = useState<string[]>([]);
  const [allCarriers, setAllCarriers] = useState<CarrierName[]>([]);
  const [filteredCarrierOptions, setFilteredCarrierOptions] = useState<Array<CarrierName>>([]);
  const [isDrawerVisibie, setIsDrawerVisible] = useState(false);

  useQuery("carrierOptions", () => getCarrierOptions(), {
    refetchOnWindowFocus: false,
    cacheTime: 60,
    onSuccess: (carrierOptionList) => {
      const carrierNameList = carrierOptionList.map((carrierOption) => carrierOption.carrier_name);
      const uniqueCarrierNameList = carrierNameList
        .filter((x, i, a) => a.indexOf(x) === i)
        .sort((a, b) => a.localeCompare(b));
      const carrierNameMap = uniqueCarrierNameList.map((carrierName) => {
        return {
          carrierName: carrierName,
        };
      });
      setAllCarriers(carrierNameMap);
      setCarrierOptions(
        uniqueCarrierNameList.filter((x) => !whitelisted.includes(x)).filter((x) => !blacklisted.includes(x))
      );
      setFilteredCarrierOptions(
        whitelisted
          .map((carrierName) => {
            return {
              carrierName: carrierName,
            };
          })
          .concat(
            blacklisted.map((carrierName) => {
              return {
                carrierName: carrierName,
              };
            })
          )
          .sort((a, b) => a.carrierName.localeCompare(b.carrierName))
      );
    },
  });

  const updateWhiteList = (carrierName: string) => {
    if (!whitelisted.includes(carrierName)) {
      whitelisted.push(carrierName);
      whitelisted.sort((a, b) => a.localeCompare(b));
      onSelectWhitelist(whitelisted);
    }
    if (blacklisted.includes(carrierName)) {
      blacklisted.splice(blacklisted.indexOf(carrierName), 1);
      onSelectBlacklist(blacklisted);
    }
  };

  const updateBlackList = (carrierName: string) => {
    if (!blacklisted.includes(carrierName)) {
      blacklisted.push(carrierName);
      blacklisted.sort((a, b) => a.localeCompare(b));
      onSelectBlacklist(blacklisted);
    }
    if (whitelisted.includes(carrierName)) {
      whitelisted.splice(whitelisted.indexOf(carrierName), 1);
      onSelectWhitelist(whitelisted);
    }
  };

  const selectChange = (selectedValue: string, carrierName: string) => {
    switch (selectedValue) {
      case "Prefer":
        updateWhiteList(carrierName);
        break;
      case "Exclude":
        updateBlackList(carrierName);
        break;
    }
  };

  const showDrawer = () => {
    setIsDrawerVisible(true);
  };

  const onClose = () => {
    setIsDrawerVisible(false);
  };

  const removePreference = (carrierName: CarrierName) => {
    const newFilteredCarrierOptions = cloneDeep(filteredCarrierOptions).map((x) => x.carrierName);
    const newCarrierOptions = cloneDeep(carrierOptions);
    if (newFilteredCarrierOptions.indexOf(carrierName.carrierName) !== -1) {
      newFilteredCarrierOptions.splice(newFilteredCarrierOptions.indexOf(carrierName.carrierName), 1);
    }
    newCarrierOptions.push(carrierName.carrierName);
    setFilteredCarrierOptions(
      newFilteredCarrierOptions.map((x) => {
        return {
          carrierName: x,
        };
      })
    );
    setCarrierOptions(newCarrierOptions);
    if (whitelisted.indexOf(carrierName.carrierName) !== -1) {
      whitelisted.splice(whitelisted.indexOf(carrierName.carrierName), 1);
    }
    if (blacklisted.indexOf(carrierName.carrierName) !== -1) {
      blacklisted.splice(blacklisted.indexOf(carrierName.carrierName), 1);
    }
    onSelectWhitelist(whitelisted);
    onSelectBlacklist(blacklisted);
  };

  const addCarrierFilter = (carrierName: string) => {
    const newFilteredCarrierOptions = cloneDeep(filteredCarrierOptions);
    const newCarrierOptions = cloneDeep(carrierOptions);
    newCarrierOptions.splice(newCarrierOptions.indexOf(carrierName), 1);
    newFilteredCarrierOptions.push({
      carrierName,
    });
    whitelisted.push(carrierName);
    whitelisted.sort((a, b) => a.localeCompare(b));
    onSelectWhitelist(whitelisted);
    setFilteredCarrierOptions(newFilteredCarrierOptions);
    setCarrierOptions(newCarrierOptions);

    return filteredCarrierOptions;
  };

  return (
    <S.Wrapper>
      <S.Header>Shipping Preferences</S.Header>
      <Text>
        <S.AllCarriersSection>
          <b>Carrier Preferences</b>
          <Button type="link" onClick={() => showDrawer()}>
            View All Carriers
          </Button>
        </S.AllCarriersSection>
      </Text>
      <Text type="secondary">Add a carrier and indicate whether it is a preferred or excluded carrier.</Text>

      <ShippingPreferencesSearch
        carrierOptions={carrierOptions}
        addCarrierFilter={addCarrierFilter}
      ></ShippingPreferencesSearch>
      <br />
      <ShippingPreferencesDrawer
        isDrawerVisible={isDrawerVisibie}
        onClose={onClose}
        allCarriers={allCarriers}
        addCarrierFilter={addCarrierFilter}
        removePreference={removePreference}
        selectChange={selectChange}
        whitelisted={whitelisted}
        blacklisted={blacklisted}
      ></ShippingPreferencesDrawer>

      <ShippingPreferencesTable
        selectChange={selectChange}
        removePreference={removePreference}
        whitelisted={whitelisted}
        blacklisted={blacklisted}
        dataSource={filteredCarrierOptions}
      />
    </S.Wrapper>
  );
};

export default ShippingPreferencesSection;
